import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import ContentContainer from "../../../templates/content-container/contentContainer";
import { PAGE_IDS } from "../../../app/data/common/pageIds";
import ShippingDocsService from "app/data/shipping-docs/shippingDocsService";
import { getPdfDownloadErrorMessage, deliverPdf } from "app/data/shipping-docs/downloadPdf";
import { PdfDocumentAction } from "app/data/shipping-docs/pdfDocumentActionStorage";
import TrackingService from "app/data/tracking/trackingService";
import ProbillChipInput from "./probillChipInput";
import DocumentSelectionTable from "./documentSelectionTable";
import DownloadActionBar from "./downloadActionBar";
import {
  DEFAULT_DOC_SELECTION,
  DocTypeDownloadStatusMap,
  EMPTY_DOC_DOWNLOAD_STATUS,
  MAX_MANIFEST_BATCH,
  ProbillEntry,
  ProbillSelectionMap,
  extractDeliveryManifestNumber,
} from "./types";

import "./labelsReceipts.scss";

const shippingDocsService = ShippingDocsService.getInstance();
const trackingService = TrackingService.getInstance();

const LabelsReceipts: React.FC = () => {
  const [entries, setEntries] = useState<ProbillEntry[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [selection, setSelection] = useState<ProbillSelectionMap>({});
  const [downloadStatus, setDownloadStatus] = useState<DocTypeDownloadStatusMap>(
    EMPTY_DOC_DOWNLOAD_STATUS
  );
  const validatingIdsRef = useRef<Set<string>>(new Set());

  const validProbills = useMemo(
    () =>
      entries
        .filter((e) => e.status === "valid" && e.value != null)
        .map((e) => e.value as number),
    [entries]
  );

  const deliveryManifestByProbill = useMemo(() => {
    const map: Record<number, number | undefined> = {};
    for (const entry of entries) {
      if (entry.status === "valid" && entry.value != null) {
        map[entry.value] = entry.deliveryManifestNumber;
      }
    }
    return map;
  }, [entries]);

  useEffect(() => {
    setSelection((prev) => {
      const next: ProbillSelectionMap = {};
      for (const probill of validProbills) {
        const existing = prev[probill];
        next[probill] = existing
          ? {
              ...DEFAULT_DOC_SELECTION,
              ...existing,
              deliveryManifest: deliveryManifestByProbill[probill]
                ? !!existing.deliveryManifest
                : false,
            }
          : { ...DEFAULT_DOC_SELECTION };
      }
      return next;
    });
  }, [validProbills, deliveryManifestByProbill]);

  useEffect(() => {
    const pending = entries.filter(
      (e) => e.status === "validating" && e.value != null && !validatingIdsRef.current.has(e.id)
    );
    if (!pending.length) return;

    pending.forEach((entry) => {
      validatingIdsRef.current.add(entry.id);
      const probillNumber = entry.value as number;

      trackingService
        .getShipmentDetails(probillNumber)
        .then((response) => {
          const exists = response?.ok?.() && !!response.data;
          const deliveryManifestNumber = exists
            ? extractDeliveryManifestNumber(response.data)
            : undefined;
          setEntries((current) =>
            current.map((item) => {
              if (item.id !== entry.id) return item;
              if (item.status !== "validating") return item;
              return exists
                ? {
                    ...item,
                    status: "valid" as const,
                    errorMessage: undefined,
                    deliveryManifestNumber,
                  }
                : {
                    ...item,
                    status: "invalid" as const,
                    errorMessage: "Probill not found",
                    deliveryManifestNumber: undefined,
                  };
            })
          );
        })
        .catch(() => {
          setEntries((current) =>
            current.map((item) => {
              if (item.id !== entry.id) return item;
              if (item.status !== "validating") return item;
              return {
                ...item,
                status: "invalid" as const,
                errorMessage: "Probill not found",
                deliveryManifestNumber: undefined,
              };
            })
          );
        })
        .finally(() => {
          validatingIdsRef.current.delete(entry.id);
        });
    });
  }, [entries]);

  const driverReceiptProbills = useMemo(
    () => validProbills.filter((p) => selection[p]?.driverReceipt),
    [validProbills, selection]
  );

  const shippingLabelProbills = useMemo(
    () => validProbills.filter((p) => selection[p]?.shippingLabel),
    [validProbills, selection]
  );

  const uniqueSelectedManifestNumbers = useMemo(() => {
    const seen = new Set<number>();
    const values: number[] = [];
    for (const probill of validProbills) {
      const mf = deliveryManifestByProbill[probill];
      if (mf && selection[probill]?.deliveryManifest && !seen.has(mf)) {
        seen.add(mf);
        values.push(mf);
      }
    }
    return values;
  }, [validProbills, deliveryManifestByProbill, selection]);

  const isDownloading =
    downloadStatus.shippingLabels === "queued" ||
    downloadStatus.shippingLabels === "downloading" ||
    downloadStatus.driverReceipts === "queued" ||
    downloadStatus.driverReceipts === "downloading" ||
    downloadStatus.deliveryManifests === "queued" ||
    downloadStatus.deliveryManifests === "downloading";

  const clearSelection = () => {
    const next: ProbillSelectionMap = {};
    for (const probill of validProbills) {
      next[probill] = { ...DEFAULT_DOC_SELECTION };
    }
    setSelection(next);
    setDownloadStatus(EMPTY_DOC_DOWNLOAD_STATUS);
  };

  const selectAllDocuments = () => {
    const next: ProbillSelectionMap = {};
    for (const probill of validProbills) {
      const hasManifest = !!deliveryManifestByProbill[probill];
      next[probill] = {
        driverReceipt: true,
        shippingLabel: true,
        deliveryManifest: hasManifest,
      };
    }
    setSelection(next);
  };

  const handleDeliver = async (action: PdfDocumentAction) => {
    if (isDownloading) return;

    const wantLabels = shippingLabelProbills.length > 0;
    const wantReceipts = driverReceiptProbills.length > 0;
    const wantManifests = uniqueSelectedManifestNumbers.length > 0;

    if (!wantLabels && !wantReceipts && !wantManifests) return;

    if (uniqueSelectedManifestNumbers.length > MAX_MANIFEST_BATCH) {
      toast.error(
        `Select up to ${MAX_MANIFEST_BATCH} unique delivery manifests per download.`
      );
      return;
    }

    const pastVerb = action === "print" ? "printed" : "downloaded";
    const failVerb = action === "print" ? "print" : "download";

    setDownloadStatus({
      shippingLabels: wantLabels ? "queued" : "idle",
      driverReceipts: wantReceipts ? "queued" : "idle",
      deliveryManifests: wantManifests ? "queued" : "idle",
    });

    let labelsSucceeded = false;
    let receiptsSucceeded = false;
    let manifestsSucceeded = false;

    if (wantLabels) {
      setDownloadStatus((prev) => ({ ...prev, shippingLabels: "downloading" }));
      try {
        const response = await shippingDocsService.downloadShippingLabels({
          probillNumbers: shippingLabelProbills,
        });
        deliverPdf(
          response.data,
          `shipping-labels_${shippingLabelProbills[0]}.pdf`,
          action
        );
        labelsSucceeded = true;
        setDownloadStatus((prev) => ({ ...prev, shippingLabels: "success" }));
      } catch (error) {
        setDownloadStatus((prev) => ({ ...prev, shippingLabels: "error" }));
        toast.error(
          getPdfDownloadErrorMessage(error, `Unable to ${failVerb} shipment labels.`)
        );
      }
    }

    if (wantReceipts) {
      setDownloadStatus((prev) => ({ ...prev, driverReceipts: "downloading" }));
      try {
        const response = await shippingDocsService.downloadDriverReceipts({
          probillNumbers: driverReceiptProbills,
        });
        deliverPdf(
          response.data,
          `driver-receipts_${driverReceiptProbills[0]}.pdf`,
          action
        );
        receiptsSucceeded = true;
        setDownloadStatus((prev) => ({ ...prev, driverReceipts: "success" }));
      } catch (error) {
        setDownloadStatus((prev) => ({ ...prev, driverReceipts: "error" }));
        toast.error(
          getPdfDownloadErrorMessage(error, `Unable to ${failVerb} driver receipts.`)
        );
      }
    }

    if (wantManifests) {
      setDownloadStatus((prev) => ({ ...prev, deliveryManifests: "downloading" }));
      try {
        const response = await shippingDocsService.downloadDeliveryManifestsBatch({
          manifestNumbers: uniqueSelectedManifestNumbers,
        });
        deliverPdf(
          response.data,
          `delivery-manifests_${uniqueSelectedManifestNumbers[0]}.pdf`,
          action
        );
        manifestsSucceeded = true;
        setDownloadStatus((prev) => ({ ...prev, deliveryManifests: "success" }));
      } catch (error) {
        setDownloadStatus((prev) => ({ ...prev, deliveryManifests: "error" }));
        toast.error(
          getPdfDownloadErrorMessage(error, `Unable to ${failVerb} delivery manifests.`)
        );
      }
    }

    if (labelsSucceeded) {
      toast.info(`Shipment labels ${pastVerb}.`);
    }
    if (receiptsSucceeded) {
      toast.info(
        driverReceiptProbills.length === 1
          ? `Driver receipt ${pastVerb}.`
          : `Driver receipts ${pastVerb}.`
      );
    }
    if (manifestsSucceeded) {
      toast.info(
        uniqueSelectedManifestNumbers.length === 1
          ? `Delivery manifest ${pastVerb}.`
          : `Delivery manifests ${pastVerb}.`
      );
    }
  };

  return (
    <ContentContainer pageId={PAGE_IDS.LABELS_RECEIPTS} title="Print & Download">
      <div className="xgs-print-download">
        <section className="xgs-print-download__card">
          <div className="xgs-print-download__card__header">
            <span className="xgs-print-download__step">1</span>
            <div>
              <div className="xgs-print-download__card__title">Enter Probill Numbers</div>
              <div className="xgs-print-download__card__desc">
                Type or paste one or more probill numbers. Each will be validated automatically.
              </div>
            </div>
          </div>
          <ProbillChipInput
            entries={entries}
            onEntriesChange={setEntries}
            inputValue={inputValue}
            onInputValueChange={setInputValue}
          />
        </section>

        {validProbills.length > 0 && (
          <section className="xgs-print-download__card">
            <div className="xgs-print-download__card__header">
              <span className="xgs-print-download__step">2</span>
              <div className="xgs-print-download__card__header-text">
                <div className="xgs-print-download__card__title">Select Documents to Download</div>
                <div className="xgs-print-download__card__desc">
                  Check any combination of probills and document types. Mix and match freely.
                </div>
              </div>
              <button
                type="button"
                className="xgs-print-download__link xgs-print-download__card__select-all"
                onClick={selectAllDocuments}
              >
                Select all
              </button>
            </div>
            <DocumentSelectionTable
              validProbills={validProbills}
              deliveryManifestByProbill={deliveryManifestByProbill}
              selection={selection}
              onSelectionChange={setSelection}
            />
          </section>
        )}

        <DownloadActionBar
          driverReceiptCount={driverReceiptProbills.length}
          shippingLabelCount={shippingLabelProbills.length}
          deliveryManifestCount={uniqueSelectedManifestNumbers.length}
          downloadStatus={downloadStatus}
          onClearSelection={clearSelection}
          onDeliver={handleDeliver}
        />
      </div>
    </ContentContainer>
  );
};

export default LabelsReceipts;
